/**
 * Notification Store - State management for real-time notifications
 * Manages notification state, preferences, and history
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Notification {
  id: string;
  type: 'intervention_assigned' | 'intervention_escalated' | 'intervention_required';
  title: string;
  message: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  interventionId: string;
  projectId: string;
  interventionType?: string;
  requestingAgent?: string;
  timestamp: string;
  read: boolean;
  quickApprove?: boolean;
}

export interface NotificationPreferences {
  browserNotifications: boolean;
  soundAlerts: boolean;
  inAppNotifications: boolean;
  notificationFrequency: 'IMMEDIATE' | 'BATCHED';
  quietHoursEnabled: boolean;
  quietHoursStart: string; // HH:MM format
  quietHoursEnd: string; // HH:MM format
  enabledTypes: {
    intervention_assigned: boolean;
    intervention_escalated: boolean;
    intervention_required: boolean;
  };
}

interface NotificationState {
  // State
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences;
  isPermissionGranted: boolean;
  queuedNotifications: Notification[];
  activePopups: Notification[];

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => Notification;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: string) => void;
  clearOldNotifications: () => void;
  updatePreferences: (preferences: Partial<NotificationPreferences>) => void;
  setPermissionGranted: (granted: boolean) => void;
  addToPopupQueue: (notification: Notification) => void;
  removeFromPopupQueue: (notificationId: string) => void;
  getBadgeCount: () => number;
}

const defaultPreferences: NotificationPreferences = {
  browserNotifications: true,
  soundAlerts: true,
  inAppNotifications: true,
  notificationFrequency: 'IMMEDIATE',
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
  enabledTypes: {
    intervention_assigned: true,
    intervention_escalated: true,
    intervention_required: true,
  },
};

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      // Initial state
      notifications: [],
      unreadCount: 0,
      preferences: defaultPreferences,
      isPermissionGranted: false,
      queuedNotifications: [],
      activePopups: [],

      // Add new notification
      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date().toISOString(),
          read: false,
        };

        set((state) => ({
          notifications: [newNotification, ...state.notifications].slice(0, 100), // Keep last 100
          unreadCount: state.unreadCount + 1,
        }));

        return newNotification;
      },

      // Mark notification as read
      markAsRead: (notificationId) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === notificationId);
          if (!notification || notification.read) return state;

          return {
            notifications: state.notifications.map((n) =>
              n.id === notificationId ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },

      // Mark all as read
      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      // Remove notification
      removeNotification: (notificationId) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === notificationId);
          const wasUnread = notification && !notification.read;

          return {
            notifications: state.notifications.filter((n) => n.id !== notificationId),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          };
        });
      },

      // Clear notifications older than 7 days
      clearOldNotifications: () => {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        set((state) => {
          const filtered = state.notifications.filter(
            (n) => new Date(n.timestamp) > sevenDaysAgo
          );
          const removedUnread = state.notifications.filter(
            (n) => new Date(n.timestamp) <= sevenDaysAgo && !n.read
          ).length;

          return {
            notifications: filtered,
            unreadCount: Math.max(0, state.unreadCount - removedUnread),
          };
        });
      },

      // Update preferences
      updatePreferences: (preferences) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            ...preferences,
          },
        }));
      },

      // Set browser notification permission
      setPermissionGranted: (granted) => {
        set({ isPermissionGranted: granted });
      },

      // Add to popup queue
      addToPopupQueue: (notification) => {
        set((state) => ({
          activePopups: [...state.activePopups, notification].slice(-3), // Max 3 popups
        }));
      },

      // Remove from popup queue
      removeFromPopupQueue: (notificationId) => {
        set((state) => ({
          activePopups: state.activePopups.filter((n) => n.id !== notificationId),
        }));
      },

      // Get badge count (pending interventions)
      getBadgeCount: () => {
        return get().unreadCount;
      },
    }),
    {
      name: 'notification-storage',
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
        preferences: state.preferences,
        isPermissionGranted: state.isPermissionGranted,
      }),
    }
  )
);

export default useNotificationStore;
